import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Info, Upload, FileText, HelpCircle, AlertTriangle } from 'lucide-react';
import { useApi } from '../hooks/useUser.jsx';
import {
  WORKER_URL,
  DEFAULT_CONFIG,
  getSamlAcsUrl,
  getOidcCallbackUrl,
  getSamlMetadataUrl,
} from '../config.js';

/**
 * Configuration Form Page
 *
 * Create or edit SAML/OIDC configurations with preloaded playground.idnty.one defaults
 *
 * IMPORTANT: SAML ACS and OIDC callback URLs MUST point to the worker URL,
 * not the frontend domain. The frontend (Cloudflare Pages) cannot handle
 * POST requests, which is why you get 405 errors if the URLs are wrong.
 */
export function ConfigForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const { fetchWithUser, userId } = useApi();
  
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [metadataInput, setMetadataInput] = React.useState('');
  const [showMetadataInput, setShowMetadataInput] = React.useState(false);
  const [activeIdpTab, setActiveIdpTab] = React.useState('okta');
  const [showIdpGuide, setShowIdpGuide] = React.useState(false);
  
  // Use centralized config for defaults
  // These URLs point to the worker, which handles protocol endpoints
  const PLAYGROUND_DEFAULTS = DEFAULT_CONFIG;
  
  const [formData, setFormData] = React.useState({
    name: '',
    description: '',
    type: 'oidc',
    config: {
      // OIDC defaults
      client: {
        clientId: '',
        clientSecret: '',
        redirectUri: PLAYGROUND_DEFAULTS.oidc.redirectUri,
        scopes: ['openid', 'profile', 'email'],
      },
      provider: {
        issuer: '',
        discoveryUrl: '',
      },
      security: {
        usePkce: true,
        codeChallengeMethod: 'S256',
      },
      claimsMapping: {},
      // SAML defaults
      sp: {
        entityId: PLAYGROUND_DEFAULTS.saml.sp.entityId,
        acsUrl: PLAYGROUND_DEFAULTS.saml.sp.acsUrl,
        nameIdFormat: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
      },
      idp: {
        entityId: '',
        ssoUrl: '',
        certificate: '',
      },
    },
    isActive: true,
  });
  
  React.useEffect(() => {
    if (isEditing && userId) {
      loadConfig();
    }
  }, [id, userId]);
  
  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await fetchWithUser(`/api/configs/${id}`);
      if (!response.ok) throw new Error('Failed to load configuration');
      const data = await response.json();
      setFormData(data.config);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const url = isEditing ? `/api/configs/${id}` : '/api/configs';
      const method = isEditing ? 'PUT' : 'POST';
      
      const response = await fetchWithUser(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save configuration');
      }
      
      navigate('/configs');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };
  
  const handleConfigChange = (path, value) => {
    setFormData(prev => {
      const newConfig = { ...prev.config };
      const keys = path.split('.');
      let current = newConfig;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      
      return { ...prev, config: newConfig };
    });
  };
  
  /**
   * Parse SAML metadata XML and extract IdP configuration
   */
  const parseSamlMetadata = (xml) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(xml, 'text/xml');
      
      // Check for parsing errors
      const parseError = doc.querySelector('parsererror');
      if (parseError) {
        throw new Error('Invalid XML format');
      }
      
      // Extract Entity ID
      const entityDescriptor = doc.querySelector('EntityDescriptor');
      const entityId = entityDescriptor?.getAttribute('entityID');
      
      // Extract IDPSSODescriptor
      const idpSsoDescriptor = doc.querySelector('IDPSSODescriptor');
      
      // Extract SSO URL
      const ssoService = idpSsoDescriptor?.querySelector('SingleSignOnService');
      const ssoUrl = ssoService?.getAttribute('Location');
      
      // Extract Name ID Format
      const nameIdFormat = idpSsoDescriptor?.querySelector('NameIDFormat')?.textContent;
      
      // Extract X.509 Certificate
      const certificate = idpSsoDescriptor?.querySelector('X509Certificate')?.textContent?.trim();
      
      if (!entityId || !ssoUrl) {
        throw new Error('Could not find required IdP information in metadata');
      }
      
      return {
        entityId,
        ssoUrl,
        nameIdFormat,
        certificate: certificate ? `-----BEGIN CERTIFICATE-----\n${certificate}\n-----END CERTIFICATE-----` : '',
      };
    } catch (err) {
      throw new Error(`Failed to parse metadata: ${err.message}`);
    }
  };
  
  /**
   * Handle metadata file upload
   */
  const handleMetadataUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const xml = event.target?.result;
        const parsed = parseSamlMetadata(xml);
        
        // Update form with parsed metadata
        setFormData(prev => ({
          ...prev,
          config: {
            ...prev.config,
            idp: {
              ...prev.config.idp,
              entityId: parsed.entityId,
              ssoUrl: parsed.ssoUrl,
              certificate: parsed.certificate,
            },
            sp: {
              ...prev.config.sp,
              nameIdFormat: parsed.nameIdFormat || 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
            },
          },
        }));
        
        setMetadataInput('');
        setShowMetadataInput(false);
        setError(null);
      } catch (err) {
        setError(err.message);
      }
    };
    reader.readAsText(file);
  };
  
  /**
   * Handle metadata paste
   */
  const handleMetadataPaste = () => {
    try {
      const parsed = parseSamlMetadata(metadataInput);
      
      setFormData(prev => ({
        ...prev,
        config: {
          ...prev.config,
          idp: {
            ...prev.config.idp,
            entityId: parsed.entityId,
            ssoUrl: parsed.ssoUrl,
            certificate: parsed.certificate,
          },
          sp: {
            ...prev.config.sp,
            nameIdFormat: parsed.nameIdFormat || 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
          },
        },
      }));
      
      setMetadataInput('');
      setShowMetadataInput(false);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };
  
  return (
    <div className="animate-fade-in max-w-4xl mx-auto py-12 px-4">
      <div className="flex items-center justify-between mb-10">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/configs')}
            className="p-3 hover:bg-[#EEEEEE] rounded-full transition-colors text-[#333333]"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-[#333333]">
              {isEditing ? 'Edit Configuration' : 'New Configuration'}
            </h1>
            <p className="text-[#666666] mt-1">
              {isEditing ? 'Update your SAML or OIDC configuration' : 'Create a new SAML or OIDC test configuration'}
            </p>
          </div>
        </div>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="bg-[#D4AF37] text-white px-8 py-4 rounded-md font-bold text-lg hover:bg-[#B8962E] transition-colors flex items-center disabled:opacity-50"
        >
          <Save className="mr-2 h-5 w-5" />
          {loading ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
      
      {error && (
        <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="card-heavy p-8 bg-white border-2 border-[#EEEEEE]">
          <h2 className="text-xl font-bold mb-6 text-[#D4AF37] uppercase tracking-wider">Basic Information</h2>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-bold text-[#333333] uppercase tracking-widest mb-2">Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-[#EEEEEE] rounded-md focus:border-[#D4AF37] outline-none transition-colors"
                placeholder="My OIDC Provider"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-bold text-[#333333] uppercase tracking-widest mb-2">Description</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-[#EEEEEE] rounded-md focus:border-[#D4AF37] outline-none transition-colors"
                placeholder="Optional description"
              />
            </div>
            
            <div>
              <label className="block text-sm font-bold text-[#333333] uppercase tracking-widest mb-2">Protocol *</label>
              <div className="grid grid-cols-2 gap-6">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, type: 'oidc' }))}
                  className={`p-6 border-2 rounded-lg text-left transition-all ${
                    formData.type === 'oidc'
                      ? 'border-[#D4AF37] bg-[#D4AF37]/5'
                      : 'border-[#EEEEEE] hover:border-[#D4AF37]/50'
                  }`}
                >
                  <div className="font-bold text-lg text-[#333333]">OIDC</div>
                  <div className="text-sm text-[#666666]">OpenID Connect</div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, type: 'saml' }))}
                  className={`p-6 border-2 rounded-lg text-left transition-all ${
                    formData.type === 'saml'
                      ? 'border-[#D4AF37] bg-[#D4AF37]/5'
                      : 'border-[#EEEEEE] hover:border-[#D4AF37]/50'
                  }`}
                >
                  <div className="font-bold text-lg text-[#333333]">SAML</div>
                  <div className="text-sm text-[#666666]">Security Assertion Markup Language</div>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* OIDC Configuration */}
        {formData.type === 'oidc' && (
          <div className="card-heavy p-8 bg-white border-2 border-[#EEEEEE]">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#D4AF37] uppercase tracking-wider">OIDC Configuration</h2>
              <button
                type="button"
                onClick={() => setShowIdpGuide(!showIdpGuide)}
                className="text-[#D4AF37] hover:text-[#B8962E] font-bold flex items-center transition-colors"
              >
                <HelpCircle className="h-5 w-5 mr-2" />
                IdP Setup Guide
              </button>
            </div>
            
            {/* IdP Guide Tabs */}
            {showIdpGuide && (
              <IdpGuideTabs 
                activeTab={activeIdpTab} 
                setActiveTab={setActiveIdpTab}
                protocol="oidc"
              />
            )}
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-[#333333] uppercase tracking-widest mb-2">
                  Issuer URL *
                  <Info className="inline h-4 w-4 ml-2 text-[#999999]" />
                </label>
                <input
                  type="url"
                  value={formData.config.provider?.issuer || ''}
                  onChange={(e) => handleConfigChange('provider.issuer', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-[#EEEEEE] rounded-md focus:border-[#D4AF37] outline-none transition-colors"
                  placeholder="https://accounts.google.com"
                  required
                />
                <p className="text-xs text-[#999999] mt-2 font-medium">
                  The OIDC issuer URL. Discovery will be auto-configured.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-[#333333] uppercase tracking-widest mb-2">Client ID *</label>
                <input
                  type="text"
                  value={formData.config.client?.clientId || ''}
                  onChange={(e) => handleConfigChange('client.clientId', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-[#EEEEEE] rounded-md focus:border-[#D4AF37] outline-none transition-colors"
                  placeholder="your-client-id"
                  required
                />
              </div>
              
              <div>
                <label className="label block mb-2">Client Secret</label>
                <input
                  type="password"
                  value={formData.config.client?.clientSecret || ''}
                  onChange={(e) => handleConfigChange('client.clientSecret', e.target.value)}
                  className="input w-full"
                  placeholder="your-client-secret"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Optional for public clients using PKCE
                </p>
              </div>
              
              <div>
                <label className="label block mb-2">
                  Redirect URI
                  <span className="ml-2 text-xs text-primary font-normal">(Worker URL)</span>
                </label>
                <input
                  type="url"
                  value={formData.config.client?.redirectUri || ''}
                  onChange={(e) => handleConfigChange('client.redirectUri', e.target.value)}
                  className="input w-full bg-muted/50"
                  placeholder="https://playground.idnty.one/oidc/callback"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  This is your callback URL. Register this in your IdP settings.
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="usePkce"
                  checked={formData.config.security?.usePkce || false}
                  onChange={(e) => handleConfigChange('security.usePkce', e.target.checked)}
                  className="rounded border-input"
                />
                <label htmlFor="usePkce" className="text-sm">
                  Use PKCE (recommended for public clients)
                </label>
              </div>
            </div>
          </div>
        )}
        
        {/* SAML Configuration */}
        {formData.type === 'saml' && (
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-primary">SAML Configuration</h2>
              <button
                type="button"
                onClick={() => setShowIdpGuide(!showIdpGuide)}
                className="btn btn-ghost btn-sm text-primary"
              >
                <HelpCircle className="h-4 w-4 mr-1" />
                IdP Setup Guide
              </button>
            </div>
            
            {/* IdP Guide Tabs */}
            {showIdpGuide && (
              <IdpGuideTabs 
                activeTab={activeIdpTab} 
                setActiveTab={setActiveIdpTab}
                protocol="saml"
              />
            )}
            
            {/* Metadata Upload Section */}
            <div className="mb-6 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium">Import IdP Metadata</h3>
                <button
                  type="button"
                  onClick={() => setShowMetadataInput(!showMetadataInput)}
                  className="text-sm text-primary hover:underline"
                >
                  {showMetadataInput ? 'Hide' : 'Upload/Paste Metadata'}
                </button>
              </div>
              
              {showMetadataInput && (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <label className="flex-1 cursor-pointer">
                      <input
                        type="file"
                        accept=".xml"
                        onChange={handleMetadataUpload}
                        className="hidden"
                      />
                      <div className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-border rounded-lg hover:border-primary transition-colors">
                        <Upload className="h-4 w-4" />
                        <span className="text-sm">Upload XML File</span>
                      </div>
                    </label>
                  </div>
                  
                  <div className="text-center text-xs text-muted-foreground">or</div>
                  
                  <div>
                    <textarea
                      value={metadataInput}
                      onChange={(e) => setMetadataInput(e.target.value)}
                      className="input w-full h-32 font-mono text-xs"
                      placeholder="Paste SAML metadata XML here..."
                    />
                    <button
                      type="button"
                      onClick={handleMetadataPaste}
                      disabled={!metadataInput.trim()}
                      className="btn btn-outline btn-sm mt-2"
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      Parse Metadata
                    </button>
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    Upload or paste your IdP's SAML metadata XML to auto-fill the configuration.
                  </p>
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              {/* SP Configuration - Pre-filled */}
              <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg mb-4">
                <h3 className="text-sm font-medium text-primary mb-3">Service Provider (SP) Settings</h3>
                <p className="text-xs text-muted-foreground mb-3">
                  ACS URL points to the worker. Use these values when configuring your IdP.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="label block mb-2">SP Entity ID *</label>
                    <input
                      type="text"
                      value={formData.config.sp?.entityId || ''}
                      onChange={(e) => handleConfigChange('sp.entityId', e.target.value)}
                      className="input w-full bg-muted/50"
                      placeholder="https://playground.idnty.one/saml"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="label block mb-2">ACS URL *</label>
                    <input
                      type="url"
                      value={formData.config.sp?.acsUrl || ''}
                      onChange={(e) => handleConfigChange('sp.acsUrl', e.target.value)}
                      className="input w-full bg-muted/50"
                      placeholder="https://playground.idnty.one/saml/acs"
                      required
                    />
                  </div>
                </div>
              </div>
              
              {/* IdP Configuration */}
              <div>
                <h3 className="text-sm font-medium mb-3">Identity Provider (IdP) Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="label block mb-2">IdP SSO URL *</label>
                    <input
                      type="url"
                      value={formData.config.idp?.ssoUrl || ''}
                      onChange={(e) => handleConfigChange('idp.ssoUrl', e.target.value)}
                      className="input w-full"
                      placeholder="https://idp.example.com/sso"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="label block mb-2">IdP Entity ID *</label>
                    <input
                      type="text"
                      value={formData.config.idp?.entityId || ''}
                      onChange={(e) => handleConfigChange('idp.entityId', e.target.value)}
                      className="input w-full"
                      placeholder="https://idp.example.com"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="label block mb-2">IdP Certificate</label>
                    <textarea
                      value={formData.config.idp?.certificate || ''}
                      onChange={(e) => handleConfigChange('idp.certificate', e.target.value)}
                      className="input w-full h-32 font-mono text-xs"
                      placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Active Status */}
        <div className="card p-6">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="isActive"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              className="rounded border-input"
            />
            <label htmlFor="isActive" className="text-sm font-medium">
              Active
            </label>
          </div>
        </div>
        
        {/* Submit */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/configs')}
            className="btn btn-outline"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-gold"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Configuration
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * IdP Guide Tabs Component
 * 
 * Shows setup guides for popular Identity Providers
 */
function IdpGuideTabs({ activeTab, setActiveTab, protocol }) {
  const tabs = [
    { id: 'okta', name: 'Okta' },
    { id: 'entra', name: 'Microsoft Entra' },
    { id: 'jumpcloud', name: 'JumpCloud' },
  ];
  
  return (
    <div className="mb-6 border border-border rounded-lg overflow-hidden">
      {/* Tab Headers */}
      <div className="flex border-b border-border bg-muted/30">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'text-primary border-b-2 border-primary bg-background'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.name}
          </button>
        ))}
      </div>
      
      {/* Tab Content */}
      <div className="p-4 bg-background">
        {activeTab === 'okta' && <OktaGuide protocol={protocol} />}
        {activeTab === 'entra' && <EntraGuide protocol={protocol} />}
        {activeTab === 'jumpcloud' && <JumpCloudGuide protocol={protocol} />}
      </div>
    </div>
  );
}

function OktaGuide({ protocol }) {
  if (protocol === 'oidc') {
    return (
      <div className="space-y-3 text-sm">
        <h4 className="font-semibold text-primary">Setting up OIDC with Okta</h4>
        <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
          <li>Go to <strong>Applications → Applications</strong> in your Okta admin console</li>
          <li>Click <strong>Create App Integration</strong> and select <strong>OIDC - OpenID Connect</strong></li>
          <li>Choose <strong>Web Application</strong> or <strong>Single-Page Application</strong></li>
          <li>Set <strong>Sign-in redirect URIs</strong> to: <code className="bg-muted px-1 rounded">https://playground.idnty.one/oidc/callback</code></li>
          <li>Copy the <strong>Client ID</strong> and <strong>Client Secret</strong></li>
          <li>Your <strong>Issuer URL</strong> is: <code className="bg-muted px-1 rounded">https://{`{your-okta-domain}`}</code></li>
        </ol>
        <p className="text-xs text-muted-foreground mt-2">
          💡 Ensure your Okta domain allows the playground redirect URI in Trusted Origins.
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-3 text-sm">
      <h4 className="font-semibold text-primary">Setting up SAML with Okta</h4>
      <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
        <li>Go to <strong>Applications → Applications</strong> in your Okta admin console</li>
        <li>Click <strong>Create App Integration</strong> and select <strong>SAML 2.0</strong></li>
        <li>Set <strong>Single sign-on URL</strong> to: <code className="bg-muted px-1 rounded">https://playground.idnty.one/saml/acs</code></li>
        <li>Set <strong>Audience URI (SP Entity ID)</strong> to: <code className="bg-muted px-1 rounded">https://playground.idnty.one/saml</code></li>
        <li>Configure attribute statements as needed (e.g., email, name)</li>
        <li>After creation, go to <strong>Sign On</strong> tab and view <strong>Identity Provider metadata</strong></li>
        <li>Copy the metadata URL or download the XML to import above</li>
      </ol>
      <p className="text-xs text-muted-foreground mt-2">
        💡 Use the metadata import feature above to auto-fill IdP settings.
      </p>
    </div>
  );
}

function EntraGuide({ protocol }) {
  if (protocol === 'oidc') {
    return (
      <div className="space-y-3 text-sm">
        <h4 className="font-semibold text-primary">Setting up OIDC with Microsoft Entra ID</h4>
        <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
          <li>Go to <strong>Azure Portal → Microsoft Entra ID → App registrations</strong></li>
          <li>Click <strong>New registration</strong></li>
          <li>Enter a name and select supported account types</li>
          <li>Add redirect URI: <code className="bg-muted px-1 rounded">https://playground.idnty.one/oidc/callback</code></li>
          <li>After registration, copy the <strong>Application (client) ID</strong></li>
          <li>Go to <strong>Certificates & secrets</strong> to create a client secret</li>
          <li>Your <strong>Issuer URL</strong> is: <code className="bg-muted px-1 rounded">https://login.microsoftonline.com/{`{tenant-id}`}/v2.0</code></li>
        </ol>
        <p className="text-xs text-muted-foreground mt-2">
          💡 For multi-tenant apps, use <code className="bg-muted px-1 rounded">common</code> instead of tenant ID.
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-3 text-sm">
      <h4 className="font-semibold text-primary">Setting up SAML with Microsoft Entra ID</h4>
      <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
        <li>Go to <strong>Azure Portal → Microsoft Entra ID → Enterprise applications</strong></li>
        <li>Click <strong>New application</strong> → <strong>Create your own application</strong></li>
        <li>Select <strong>Integrate any other application you don't find in the gallery</strong></li>
        <li>Go to <strong>Set up single sign-on</strong> → <strong>SAML</strong></li>
        <li>Set <strong>Identifier (Entity ID)</strong>: <code className="bg-muted px-1 rounded">https://playground.idnty.one/saml</code></li>
        <li>Set <strong>Reply URL (ACS URL)</strong>: <code className="bg-muted px-1 rounded">https://playground.idnty.one/saml/acs</code></li>
        <li>Download the <strong>Federation Metadata XML</strong> from the SAML configuration page</li>
        <li>Upload the metadata file using the import feature above</li>
      </ol>
      <p className="text-xs text-muted-foreground mt-2">
        💡 Configure user attributes & claims to include email, name, etc.
      </p>
    </div>
  );
}

function JumpCloudGuide({ protocol }) {
  if (protocol === 'oidc') {
    return (
      <div className="space-y-3 text-sm">
        <h4 className="font-semibold text-primary">Setting up OIDC with JumpCloud</h4>
        <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
          <li>Go to <strong>SSO Applications</strong> in your JumpCloud admin console</li>
          <li>Click <strong>Add New Application</strong> → <strong>Custom Application</strong></li>
          <li>Select <strong>OIDC</strong> as the protocol</li>
          <li>Set <strong>Redirect URIs</strong> to: <code className="bg-muted px-1 rounded">https://playground.idnty.one/oidc/callback</code></li>
          <li>Copy the <strong>Client ID</strong> and <strong>Client Secret</strong></li>
          <li>Your <strong>Issuer URL</strong> is: <code className="bg-muted px-1 rounded">https://oauth.id.jumpcloud.com</code></li>
        </ol>
        <p className="text-xs text-muted-foreground mt-2">
          💡 Assign users or groups to the application after creation.
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-3 text-sm">
      <h4 className="font-semibold text-primary">Setting up SAML with JumpCloud</h4>
      <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
        <li>Go to <strong>SSO Applications</strong> in your JumpCloud admin console</li>
        <li>Click <strong>Add New Application</strong> → <strong>Custom Application</strong></li>
        <li>Select <strong>SAML</strong> as the protocol</li>
        <li>Set <strong>SP Entity ID</strong> to: <code className="bg-muted px-1 rounded">https://playground.idnty.one/saml</code></li>
        <li>Set <strong>ACS URL</strong> to: <code className="bg-muted px-1 rounded">https://playground.idnty.one/saml/acs</code></li>
        <li>Configure attribute mapping (email, firstname, lastname)</li>
        <li>After saving, click <strong>Download Metadata</strong> to get the IdP metadata XML</li>
        <li>Upload the metadata file using the import feature above</li>
      </ol>
      <p className="text-xs text-muted-foreground mt-2">
        💡 The IdP metadata URL is also available in the application details.
      </p>
    </div>
  );
}
